<template>
  <div>
    <h5 class="title is-5">
      Schedule your Daily Standup
      for <span class="has-text-info is-italic">{{ activeTeam.teamName }}</span>
    </h5>
    <p class="u-m-b">
      The daily standup is a 15-minute daily meeting where your team
      comes together to report on their goals.
    </p>
    <p class="u-m-b">
      Currently your standup meeting is scheduled for
      <strong>{{ shapedDaysOfWeekText }}</strong>
      at <strong>{{ shapedStandupTimeText }} {{ shapedTimeZoneText }}</strong> and your team will report
      on their goals from <strong>{{ shapedStandupReportingDaysText }}</strong>
    </p>
    <div v-if="isStandupCustom">
      <!-- STANDUP DAYS -->
      <section class="section u-p-x-0 u-m-b-large">
        <h5 class="title is-5">
          Which days of the week would you like to run your standup?
        </h5>
        <div class="columns is-mobile is-marginless">
          <div
            v-for="day in TimeAndDateConstants.daysOfTheWeek"
            :key="day.id"
            :class="[
              'column',
              'has-text-centered',
              'weekday-picker__weekday-column'
            ]"
          >
            <div
              :class="[
                'button',
                'is-fullwidth',
                selectedStandupMeetingDayIds.includes(day.id) ? 'is-primary' : 'is-outlined',
                'has-text-weight-bold',
                'weekday-picker__weekday-column__text-wrapper'
              ]"
              role="checkbox"
              :aria-checked="selectedStandupMeetingDayIds.includes(day.id)"
              tabindex="0"
              @click.prevent="handleSelectStandupDay(day.id)"
              @keyup.enter="handleSelectStandupDay(day.id)"
            >
              {{ day.abbreviation }}
            </div>
          </div>
        </div>
      </section>

      <!-- STANDUP TIME -->
      <section class="section u-p-x-0 u-p-t-0 u-m-b-large">
        <h5 class="title is-5">
          What's the best time for your team to come together for standup?
        </h5>
        <b-field grouped>
          <b-field label="Time" class="u-m-b-0">
            <b-select name="standupTime" v-model="selectedStandupMeetingTimeId">
              <option
                v-for="hour in TimeAndDateConstants.businessHours"
                :value="hour.id"
                :key="hour.id"
              >
                {{ hour.label }}
              </option>
            </b-select>
          </b-field>
          <b-field label="Time Zone" expanded class="u-m-b-0">
            <b-select name="teamTimeZone" v-model="selectedTimeZoneId">
              <option
                v-for="timeZone in TimeAndDateConstants.timeZones"
                :value="timeZone.id"
                :key="timeZone.id"
              >
                {{ `${timeZone.abbreviation} ${timeZone.area}/${timeZone.location} ${timeZone.utcOffset}` }}
              </option>
            </b-select>
          </b-field>
        </b-field>
      </section>

      <!-- STANDUP FORMAT -->
      <section class="section u-p-x-0 u-pad-top-0 u-m-b">
        <h5 class="title is-5">
          What should your team report on during the standup?
        </h5>
        <b-field
          v-for="reportingDay in TimeAndDateConstants.standupReportingDays"
          :key="reportingDay.id"
          class="u-m-b-small"
        >
          <b-checkbox
            v-model="selectedStandupReportingDayIds"
            :native-value="reportingDay.id"
            :disabled="selectedStandupReportingDayIds.length === 1
              && selectedStandupReportingDayIds.includes(reportingDay.id)"
          >
            {{ titleCase(reportingDay.label) }}
            <span class="has-text-grey is-size-7">
              &nbsp;
              {{ reportingDay.isRecommended ? 'Recommended for most teams' : '' }}
            </span>
          </b-checkbox>
        </b-field>
      </section>
    </div>
    <div class="u-m-t-large">
      <b-button
        type="is-primary"
        size="is-large"
        class="is-fullwidth"
        @click="handleGoToWorkspaceClick"
        :loading="isFormInProgress"
      >
        Take me to my workspace!
      </b-button>
      <b-button
        v-if="!isStandupCustom"
        size="is-small"
        type="is-text is-paddingless"
        @click.prevent="handleCustomizeStandupClick"
      >
        I'd like to make some changes.
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as TimeAndDateConstants from '@/constants/TimeAndDateConstants';
import { titleCase } from '@/helpers/stringHelpers';

export default {
  name: 'OnboardingScheduleDailyStandupForm',

  props: {
    activeTeamId: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      TimeAndDateConstants,
      isStandupCustom: false,
      selectedStandupMeetingDayIds: null,
      selectedTimeZoneId: null,
      selectedStandupMeetingTimeId: null,
      selectedStandupReportingDayIds: null,
      isFormInProgress: false,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
    }),
    activeTeam() {
      return this.teams[this.activeTeamId];
    },
    shapedDaysOfWeekText() {
      if (!this.activeTeam) return '';

      return TimeAndDateConstants.daysOfTheWeek
        .filter(day => this.activeTeam.standupMeetingDayIds.includes(day.id))
        .map(day => day.name)
        .join(', ');
    },
    shapedStandupTimeText() {
      if (!this.activeTeam) return '';

      return TimeAndDateConstants.businessHours
        .find(hour => hour.id === this.activeTeam.standupMeetingTimeId)
        .label;
    },
    shapedStandupReportingDaysText() {
      if (!this.activeTeam) return '';

      const reportingDays = TimeAndDateConstants.standupReportingDays
        .filter(reportingDay => this.activeTeam.standupReportingDayIds.includes(reportingDay.id))
        .map(reportingDay => reportingDay.label);

      return reportingDays.length > 2 ? reportingDays.join(', ') : reportingDays.join(' and ');
    },
    shapedTimeZoneText() {
      if (!this.activeTeam) return '';

      return TimeAndDateConstants.timeZones
        .filter(timeZone => timeZone.id === this.activeTeam.timeZoneId)
        .map(timeZone => timeZone.abbreviation)[0];
    },
    customStandupPayload() {
      return {
        standupMeetingDayIds: this.selectedStandupMeetingDayIds,
        standupMeetingTimeId: this.selectedStandupMeetingTimeId,
        standupReportingDayIds: this.selectedStandupReportingDayIds,
        timeZoneId: this.selectedTimeZoneId,
      };
    },
  },

  mounted() {
    if (!this.activeTeamId) this.handleSkipThisStep();

    this.setInitialFormState();
  },

  methods: {
    ...mapActions({
      updateTeam: TeamActions.UPDATE_TEAM,
    }),
    titleCase,
    handleCustomizeStandupClick() {
      this.isStandupCustom = true;
    },
    handleSelectStandupDay(dayOfTheWeekId) {
      if (this.selectedStandupMeetingDayIds.includes(dayOfTheWeekId)) {
        this.selectedStandupMeetingDayIds = [...this.selectedStandupMeetingDayIds.filter(dayId => dayId !== dayOfTheWeekId)]; // eslint-disable-line max-len
      } else {
        this.selectedStandupMeetingDayIds.push(dayOfTheWeekId);
      }
    },
    handleGoToWorkspaceClick() {
      if (this.isStandupCustom) {
        this.handleSubmitCustomStandup();
      } else {
        this.handleSkipThisStep();
      }
    },
    handleSubmitCustomStandup() {
      this.isFormInProgress = true;
      this.updateTeam({ teamId: this.activeTeamId, updatedTeam: this.customStandupPayload })
        .then(() => {
          this.$emit('submitSuccess');
          this.isFormInProgress = false;
        })
        .catch(() => { this.isFormInProgress = false; });
    },
    handleSkipThisStep() {
      this.$emit('skipStep');
    },
    setInitialFormState() {
      this.selectedStandupReportingDayIds = [...this.activeTeam.standupReportingDayIds];
      this.selectedStandupMeetingDayIds = [...this.activeTeam.standupMeetingDayIds];
      this.selectedTimeZoneId = this.activeTeam.timeZoneId;
      this.selectedStandupMeetingTimeId = this.activeTeam.standupMeetingTimeId;
    },
  },
};
</script>

<style lang="scss" scoped>
.weekday-picker__weekday-column {
  padding: $global-whitespace-xsmall;
}
</style>
